import type { OverlayLegend } from '../render/overlayColors.ts';
import { realDataStart } from '../state/realData.ts';
import { displayHour, SNAPSHOT_OVERLAY_MODES, viewHour } from '../state/store.ts';
import { formatElevation, formatNumber, formatTimelineClock } from './format.ts';

export interface LegendProps {
  legend: OverlayLegend;
}

/** Small legend overlay in the corner of the 3D view, contents depending on the active overlay. */
export function Legend({ legend }: LegendProps) {
  // While scrubbing the timeline, only a handful of overlays can be shown from the recorded
  // snapshot (see `SNAPSHOT_OVERLAY_MODES`); the rest need the exact state computed for that hour.
  if (viewHour.value !== null && !SNAPSHOT_OVERLAY_MODES.has(legend.mode)) {
    return (
      <div class="legend">
        <div class="legend-note">
          Release the timeline to see this layer at hour {Math.round(displayHour.value)} (
          {formatTimelineClock(displayHour.value, realDataStart())})
        </div>
      </div>
    );
  }

  if (legend.note) {
    return (
      <div class="legend">
        <div class="legend-note">{legend.note}</div>
      </div>
    );
  }

  switch (legend.mode) {
    case 'natural':
      return (
        <div class="legend">
          <LegendSwatch color="#e6f0ff" label="Snow" />
          <LegendSwatch color="#4d7333" label="Vegetation" />
          <LegendSwatch color="#807a75" label="Rock (>35°)" />
          <LegendSwatch color="#295986" label="Sea" />
        </div>
      );
    case 'slope':
      return (
        <div class="legend">
          <LegendSwatch color="#8c8c85" label="<27°" />
          <LegendSwatch color="#f2d933" label="27–30°" />
          <LegendSwatch color="#f28c26" label="30–35°" />
          <LegendSwatch color="#d92626" label="35–40°" />
          <LegendSwatch color="#8c26bf" label="40–45°" />
          <LegendSwatch color="#140d14" label="≥45°" />
        </div>
      );
    case 'snowDepth': {
      const max = legend.snowDepthMax ?? 3;
      return (
        <div class="legend">
          <div class="legend-ramp legend-ramp-snowdepth" />
          <div class="legend-ramp-labels">
            <span>0 m</span>
            <span>{formatNumber(max / 2, 1)} m</span>
            <span>{formatNumber(max, 1)} m</span>
          </div>
        </div>
      );
    }
    case 'temperature': {
      const half = legend.tempHalfRange ?? 10;
      return (
        <div class="legend">
          <div class="legend-ramp legend-ramp-temperature" />
          <div class="legend-ramp-labels">
            <span>−{formatNumber(half, 0)} °C</span>
            <span>0 °C</span>
            <span>+{formatNumber(half, 0)} °C</span>
          </div>
        </div>
      );
    }
    case 'precipPhase':
      return (
        <div class="legend">
          <LegendSwatch color="#40913f" label="Rain" />
          <LegendSwatch color="#8c59bf" label="Mixed" />
          <LegendSwatch color="#d9ebff" label="Snow" />
          <div class="legend-note">0 °C level: {formatElevation(legend.zeroElevation ?? null)}</div>
          <div class="legend-note">
            +2 °C level: {formatElevation(legend.twoDegElevation ?? null)}
          </div>
        </div>
      );
    case 'stormSnow': {
      const maxCm = (legend.stormSnowMaxM ?? 0.1) * 100;
      return (
        <div class="legend">
          <div class="legend-ramp legend-ramp-stormsnow" />
          <div class="legend-ramp-labels">
            <span>0 cm</span>
            <span>{formatNumber(maxCm / 2, 0)} cm</span>
            <span>{formatNumber(maxCm, 0)} cm</span>
          </div>
          <div class="legend-note">Net new snow (fall + wind); scoured areas show as bare</div>
        </div>
      );
    }
    case 'windLoading': {
      const halfCm = (legend.windLoadingHalfM ?? 0.1) * 100;
      return (
        <div class="legend">
          <div class="legend-ramp legend-ramp-windloading" />
          <div class="legend-ramp-labels">
            <span>−{formatNumber(halfCm, 0)} cm</span>
            <span>0</span>
            <span>+{formatNumber(halfCm, 0)} cm</span>
          </div>
          <div class="legend-note">Scoured ↔ loaded by wind</div>
        </div>
      );
    }
    case 'windSpeed': {
      const max = legend.windSpeedMax ?? 5;
      return (
        <div class="legend">
          <div class="legend-ramp legend-ramp-windspeed" />
          <div class="legend-ramp-labels">
            <span>0 m/s</span>
            <span>{formatNumber(max / 2, 0)} m/s</span>
            <span>{formatNumber(max, 0)} m/s</span>
          </div>
        </div>
      );
    }
    case 'shelter': {
      const half = legend.shelterHalfDeg ?? 30;
      return (
        <div class="legend">
          <div class="legend-ramp legend-ramp-shelter" />
          <div class="legend-ramp-labels">
            <span>Exposed −{formatNumber(half, 0)}°</span>
            <span>0°</span>
            <span>Sheltered +{formatNumber(half, 0)}°</span>
          </div>
        </div>
      );
    }
    case 'stability':
    case 'naturalStability':
      return (
        <div class="legend">
          <LegendSwatch color="#d92626" label="< 1 poor" />
          <LegendSwatch color="#f28c26" label="1–1.5 fair" />
          <LegendSwatch color="#40a640" label="≥ 1.5 good" />
          <div class="legend-note">
            {legend.mode === 'stability'
              ? 'Skier stability index (SK38, Jamieson & Johnston); < 1 → skier triggering likely.'
              : 'Same classes on natural (no skier load) stability.'}
          </div>
          <div class="legend-note">Grey where slope &lt; 25° or no slab.</div>
        </div>
      );
    case 'problem':
      return (
        <div class="legend">
          <LegendSwatch color="#4fa6e0" label="New snow" />
          <LegendSwatch color="#e0a340" label="Wind slab" />
          <LegendSwatch color="#8c3dbf" label="Persistent weak layer" />
          <LegendSwatch color="#2e9e8f" label="Wet snow" />
          <div class="legend-note">
            Only shown on slope ≥ 30° with poor/fair stability, or a wet surface (wet loose).
          </div>
        </div>
      );
    case 'criticalDepth': {
      const maxCm = legend.criticalDepthMaxCm ?? 100;
      return (
        <div class="legend">
          <div class="legend-ramp legend-ramp-criticaldepth" />
          <div class="legend-ramp-labels">
            <span>0 cm</span>
            <span>{formatNumber(maxCm / 2, 0)} cm</span>
            <span>{formatNumber(maxCm, 0)} cm</span>
          </div>
          <div class="legend-note">Depth to the critical weak layer; grey where none</div>
        </div>
      );
    }
    case 'forest':
      return (
        <div class="legend">
          <div class="legend-ramp legend-ramp-forest" />
          <div class="legend-ramp-labels">
            <span>10 %</span>
            <span>50 %</span>
            <span>100 %</span>
          </div>
          <div class="legend-note">
            Crown cover (NIBIO SR16). Forest holds back flowing snow; no release above 50 %.
          </div>
        </div>
      );
    case 'propagation':
      return (
        <div class="legend">
          <div class="legend-ramp legend-ramp-propagation" />
          <div class="legend-ramp-labels">
            <span>0</span>
            <span>0.5</span>
            <span>1</span>
          </div>
          <div class="legend-note">How readily a fracture would spread; grey where none</div>
        </div>
      );
    case 'avDeposit': {
      const maxCm = legend.avDepositMaxCm ?? 20;
      return (
        <div class="legend">
          <div class="legend-ramp legend-ramp-avdeposit" />
          <div class="legend-ramp-labels">
            <span>0 cm</span>
            <span>{formatNumber(maxCm / 2, 0)} cm</span>
            <span>{formatNumber(maxCm, 0)} cm</span>
          </div>
          <div class="legend-note">Debris depth, summed across all runs</div>
        </div>
      );
    }
    case 'avNet': {
      const halfCm = legend.avNetHalfCm ?? 5;
      return (
        <div class="legend">
          <div class="legend-ramp legend-ramp-avnet" />
          <div class="legend-ramp-labels">
            <span>−{formatNumber(halfCm, 0)} cm</span>
            <span>0</span>
            <span>+{formatNumber(halfCm, 0)} cm</span>
          </div>
          <div class="legend-note">Removed (red/orange) ↔ deposited (blue), all runs</div>
        </div>
      );
    }
    case 'avOrigin':
      return (
        <div class="legend">
          <div class="legend-ramp legend-ramp-avorigin" />
          <div class="legend-ramp-labels">
            <span>Release</span>
            <span>Entrained</span>
          </div>
          <div class="legend-note">Share of deposit from entrainment, selected run</div>
        </div>
      );
    case 'avMaxSpeed': {
      const max = legend.avMaxSpeedMax ?? 40;
      return (
        <div class="legend">
          <div class="legend-ramp legend-ramp-avspeed" />
          <div class="legend-ramp-labels">
            <span>0 m/s</span>
            <span>{formatNumber(max / 2, 0)} m/s</span>
            <span>{formatNumber(max, 0)} m/s</span>
          </div>
          <div class="legend-note">Max speed reached, selected run</div>
        </div>
      );
    }
    case 'avMaxPressure':
      return (
        <div class="legend">
          <div class="legend-ramp legend-ramp-avpressure" />
          <div class="legend-ramp-labels">
            <span>0</span>
            <span>3 windows</span>
            <span>30 houses</span>
            <span>100+ kPa forest</span>
          </div>
          <div class="legend-note">Max impact pressure reached, selected run</div>
        </div>
      );
    case 'avMaxThickness': {
      const max = legend.avMaxThicknessMax ?? 3;
      return (
        <div class="legend">
          <div class="legend-ramp legend-ramp-avthickness" />
          <div class="legend-ramp-labels">
            <span>0 m</span>
            <span>{formatNumber(max / 2, 1)} m</span>
            <span>{formatNumber(max, 1)} m</span>
          </div>
          <div class="legend-note">Max flow thickness reached, selected run</div>
        </div>
      );
    }
  }
}

function LegendSwatch({ color, label }: { color: string; label: string }) {
  return (
    <div class="legend-item">
      <span class="legend-swatch" style={{ backgroundColor: color }} />
      <span>{label}</span>
    </div>
  );
}
